import { match } from 'ts-pattern';
import { getIssue } from '@/components/issues/api';
import type { IssueType } from '@/components/issues/types';
import type { IssueLinkType } from '@/lib/tasksApi';

export type LinkedIssueRef = { type: IssueLinkType; id: string };

function issueTypeToLinkType(type: IssueType): IssueLinkType {
  if (type === 'bug_report') return 'bug';
  return type;
}

function categoryToLinkType(category: string | null): IssueLinkType | null {
  return match(category)
    .with('crashes', () => 'crash' as IssueLinkType)
    .with('exceptions', () => 'exception' as IssueLinkType)
    .with('anrs', () => 'anr' as IssueLinkType)
    .with('bug_reports', () => 'bug' as IssueLinkType)
    .with('feedback', () => 'feedback_item' as IssueLinkType)
    .otherwise(() => null);
}

export function parseIssueLinkInput(raw: string): { id: string; type?: IssueLinkType } | null {
  const value = raw.trim();
  if (!value) return null;

  const typed = value.match(/^(crash|exception|anr|bug|bug_report|feedback|feedback_item|chat)[:#\s]+(.+)$/i);
  if (typed) {
    const rawType = typed[1].toLowerCase();
    return {
      type: rawType === 'bug_report' ? 'bug' : (rawType as IssueLinkType),
      id: typed[2].trim(),
    };
  }

  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(value, origin);
    const categoryType = categoryToLinkType(url.searchParams.get('category'));

    if (url.pathname.startsWith('/chats')) {
      const threadId = url.searchParams.get('threadId');
      if (threadId) return { id: threadId, type: 'chat' };
    }

    if (url.pathname.startsWith('/feedback')) {
      const id = url.searchParams.get('id');
      if (id) return { id, type: 'feedback' };
    }

    const id = url.searchParams.get('selected') ??
      url.searchParams.get('issueId') ??
      url.searchParams.get('id');
    const queryType = url.searchParams.get('issueType') as IssueLinkType | null;
    if (id) return { id, type: queryType ?? categoryType ?? undefined };
  } catch {
    // Plain IDs are handled below.
  }

  return { id: value };
}

export async function resolveIssueLinkInput(raw: string): Promise<LinkedIssueRef | null> {
  const parsed = parseIssueLinkInput(raw);
  if (!parsed?.id) return null;
  if (parsed.type) return { id: parsed.id, type: parsed.type };

  const detail = await getIssue(parsed.id);
  return { id: detail.Id, type: issueTypeToLinkType(detail.Type) };
}
